import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { storeFullTesterRecommendations, getStoredFullTesterRecommendations } from "../../../utils/recsCache";
import { requestRecommendations } from "../../../utils/requestRecommendations";
import { categoryLabels } from "../../../utils/categoryLabels";
import { levelDisplayNames } from "../../../utils/displayNameUtils";
import { getRiskLevelColor, getRiskLevelPriorityColors } from "../../../utils/riskLevelUtils";

const FaIcon = ({ icon, color = "#3b82f6", size = 16 }) => (
  <i className={`fas ${icon}`} style={{ color, fontSize: size }} />
);

const SECTION_ICON_COLOR = "#3b82f6";
const SECTION_ICON_SIZE = 18;

const sectionCardShellStyle = {
  background: "linear-gradient(145deg, var(--card-bg), rgba(59,130,246,0.05))",
  borderRadius: "16px",
  border: "1px solid var(--card-border)",
  boxShadow: "0 12px 30px rgba(15,23,42,0.08)",
  maxWidth: "100%",
  overflowX: "hidden",
};

const SectionCardIcon = ({ icon }) => (
  <div
    style={{
      width: "40px",
      height: "40px",
      borderRadius: "12px",
      background: "rgba(59,130,246,0.15)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "20px",
      color: "#3b82f6",
      flexShrink: 0,
    }}
  >
    {icon}
  </div>
);

const SectionCard = ({ children, title, icon }) => (
  <div
    style={{
      ...sectionCardShellStyle,
      padding: "24px",
      display: "flex",
      flexDirection: "column",
      gap: "16px",
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
      <SectionCardIcon icon={icon} />
      <h3 style={{ margin: 0, color: "var(--card-text)", fontSize: "18px" }}>{title}</h3>
    </div>
    {children}
  </div>
);

const SectionCardAccordion = ({ children, title, icon, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div style={{ ...sectionCardShellStyle, overflow: "hidden" }}>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          padding: "24px",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
          <SectionCardIcon icon={icon} />
          <h3 style={{ margin: 0, color: "var(--card-text)", fontSize: "18px" }}>{title}</h3>
        </div>
        <i
          className={`fas fa-chevron-${isOpen ? "up" : "down"}`}
          style={{ color: "#64748b", fontSize: "14px", flexShrink: 0 }}
          aria-hidden="true"
        />
      </button>
      {isOpen && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            padding: "0 24px 24px",
            maxWidth: "100%",
            overflowX: "hidden",
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
};

const codeSnippetStyle = {
  background: "rgba(107, 114, 128, 0.05)",
  padding: "16px",
  borderRadius: "12px",
  overflow: "hidden",
  margin: 0,
  fontSize: "12px",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  overflowWrap: "break-word",
  fontFamily: "monospace",
  color: "var(--body-text)",
  lineHeight: 1.6,
  border: "1px solid rgba(107, 114, 128, 0.1)",
};

const codeCompactStyle = {
  background: "rgba(107, 114, 128, 0.05)",
  padding: "10px",
  borderRadius: "6px",
  margin: 0,
  fontSize: "11px",
  fontFamily: "monospace",
  color: "var(--body-text)",
  overflow: "hidden",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  overflowWrap: "break-word",
  lineHeight: 1.5,
};

const getPriorityIcon = (priority) => {
  switch (priority?.toLowerCase()) {
    case "critical":
      return <FaIcon icon="fa-exclamation-triangle" color="#ef4444" size={18} />;
    case "high":
      return <FaIcon icon="fa-exclamation-triangle" color="#f59e0b" size={18} />;
    case "medium":
      return <FaIcon icon="fa-info-circle" color="#3b82f6" size={18} />;
    case "low":
      return <FaIcon icon="fa-check-circle" color="#10b981" size={18} />;
    default:
      return <FaIcon icon="fa-info-circle" color="#6b7280" size={18} />;
  }
};

const formatCategoryName = (name) => {
  return categoryLabels[name] || name.split("_").map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
};

const hexToRgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const getRiskLevelStyle = (riskLevel) => {
  const textColor = getRiskLevelColor(riskLevel);
  return {
    bg: hexToRgba(textColor, 0.06),
    border: hexToRgba(textColor, 0.15),
    text: textColor,
  };
};

const PromptsDocsRecs = ({ reportData }) => {
  const { t } = useTranslation("reports_recs", { keyPrefix: "recs.promptsDocs" });
  const [recommendationDetails, setRecommendationDetails] = useState(() => {
    const reportId = reportData?.id;
    return reportId ? getStoredFullTesterRecommendations(reportId) : null;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const reportId = reportData?.id;
    if (!reportId) {
      setRecommendationDetails(null);
      return;
    }

    const stored = getStoredFullTesterRecommendations(reportId);
    if (stored) {
      setRecommendationDetails(stored);
    } else {
      setRecommendationDetails(null);
    }
  }, [reportData?.id]);

  const fetchRecommendations = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get report ID from reportData
      const reportId = reportData?.id;
      if (!reportId) {
        throw new Error(t("reportIdNotFound"));
      }

      const result = await requestRecommendations({
        recommFor: "full",
        reportId,
        reportData,
        fallbackMessage: t("noRecommendationsAvailable"),
      });

      setRecommendationDetails(result);

      if (result && Object.keys(result).length > 0) {
        storeFullTesterRecommendations(reportId, result);
      }
    } catch (err) {
      console.error("Error fetching recommendations:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Show button to load recommendations if not loaded yet
  if (!recommendationDetails && !loading) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 40px",
        background: "var(--card-bg)",
        borderRadius: "12px",
        border: "1px solid var(--card-border)",
        marginTop: "20px"
      }}>
        <div style={{
          width: "64px",
          height: "64px",
          borderRadius: "16px",
          background: "rgba(59, 130, 246, 0.15)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "20px"
        }}>
          <i className="fas fa-lightbulb" style={{ fontSize: "28px", color: "#3b82f6" }}></i>
        </div>

        {error && (
          <div style={{
            fontSize: "14px",
            color: "#ef4444",
            marginBottom: "20px",
            padding: "12px 20px",
            background: "rgba(239, 68, 68, 0.1)",
            borderRadius: "8px",
            border: "1px solid rgba(239, 68, 68, 0.2)"
          }}>
            {t("noRecommendationsFoundTitle")}
          </div>
        )}
        <button
          onClick={fetchRecommendations}
          disabled={loading}
          style={{
            padding: "12px 32px",
            background: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
            color: "white",
            border: "none",
            borderRadius: "8px",
            fontSize: "16px",
            fontWeight: "600",
            cursor: loading ? "not-allowed" : "pointer",
            transition: "all 0.3s ease",
            boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
            opacity: loading ? 0.6 : 1
          }}
          onMouseOver={(e) => {
            if (!loading) {
              e.target.style.transform = "translateY(-2px)";
              e.target.style.boxShadow = "0 6px 16px rgba(59, 130, 246, 0.4)";
            }
          }}
          onMouseOut={(e) => {
            if (!loading) {
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "0 4px 12px rgba(59, 130, 246, 0.3)";
            }
          }}
        >
          {loading ? (
            <>
              <i className="fas fa-spinner fa-spin" style={{ marginRight: "8px" }}></i>
              {t("loading")}
            </>
          ) : (
            <>
              <i className="fas fa-download" style={{ marginRight: "8px" }}></i>
              {t("loadRecommendations")}
            </>
          )}
        </button>
      </div>
    );
  }

  // Show loading state
  if (loading) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 40px",
        background: "var(--card-bg)",
        borderRadius: "12px",
        border: "1px solid var(--card-border)",
        marginTop: "20px"
      }}>
        <div style={{
          width: "64px",
          height: "64px",
          borderRadius: "16px",
          background: "rgba(59, 130, 246, 0.15)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "20px"
        }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: "28px", color: "#3b82f6" }}></i>
        </div>
        <div style={{ fontSize: "18px", color: "var(--card-text)", fontWeight: 600 }}>
          {t("loadingRecommendations")}
        </div>
      </div>
    );
  }

  // Show error if recommendations failed to load
  if (error && !recommendationDetails) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 40px",
        background: "var(--card-bg)",
        borderRadius: "12px",
        border: "1px solid var(--card-border)",
        marginTop: "20px"
      }}>
        <div style={{
          width: "64px",
          height: "64px",
          borderRadius: "16px",
          background: "rgba(239, 68, 68, 0.15)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "20px"
        }}>
          <i className="fas fa-exclamation-triangle" style={{ fontSize: "28px", color: "#ef4444" }}></i>
        </div>
        <div style={{ fontSize: "18px", color: "var(--card-text)", fontWeight: 600, marginBottom: "20px" }}>
          {t("noRecommendationsFoundTitle")}
        </div>
        <button
          onClick={fetchRecommendations}
          style={{
            padding: "12px 32px",
            background: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
            color: "white",
            border: "none",
            borderRadius: "8px",
            fontSize: "16px",
            fontWeight: "600",
            cursor: "pointer",
            transition: "all 0.3s ease",
            boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)"
          }}
          onMouseOver={(e) => {
            e.target.style.transform = "translateY(-2px)";
            e.target.style.boxShadow = "0 6px 16px rgba(59, 130, 246, 0.4)";
          }}
          onMouseOut={(e) => {
            e.target.style.transform = "translateY(0)";
            e.target.style.boxShadow = "0 4px 12px rgba(59, 130, 246, 0.3)";
          }}
        >
          <i className="fas fa-redo" style={{ marginRight: "8px" }}></i>
          {t("retry")}
        </button>
      </div>
    );
  }

  // Show empty state if recommendations loaded but empty
  if (recommendationDetails && Object.keys(recommendationDetails).length === 0) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 40px",
        background: "var(--card-bg)",
        borderRadius: "12px",
        border: "1px solid var(--card-border)",
        marginTop: "20px"
      }}>
        <div style={{
          width: "64px",
          height: "64px",
          borderRadius: "16px",
          background: "rgba(107, 114, 128, 0.15)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "20px"
        }}>
          <i className="fas fa-compass" style={{ fontSize: "28px", color: "#6b7280" }}></i>
        </div>
        <div style={{ fontSize: "18px", color: "var(--card-text)", fontWeight: 600 }}>
          {t("noRecommendationsAvailable")}
        </div>
      </div>
    );
  }

  const recommendations = recommendationDetails;

  if (!recommendations) {
    return (
      <div style={{ textAlign: "center", padding: "40px" }}>
        <div style={{ fontSize: "16px", color: "var(--body-text)" }}>
          {t("noRecommendationsAvailable")}
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px", padding: "10px 0 40px", maxWidth: "100%", overflowX: "hidden" }}>
      {/* Section 1: Executive Summary */}
      {recommendations.executive_summary && (
        <SectionCard title={t("executiveSummary")} icon={<FaIcon icon="fa-chart-bar" color={SECTION_ICON_COLOR} size={SECTION_ICON_SIZE} />}>
          {recommendations.executive_summary.security_status && (() => {
            const status = recommendations.executive_summary.security_status;
            const displayStatus = levelDisplayNames[status] || status;
            const statusStyle = getRiskLevelPriorityColors(status);

            return (
              <div style={{
                background: statusStyle.gradient,
                borderRadius: "12px",
                padding: "28px",
                color: "white",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                gap: "14px"
              }}>
                <div style={{
                  fontSize: "12px",
                  fontWeight: 600,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  opacity: 0.9
                }}>
                  {t("overallRisk")}
                </div>
                <div style={{
                  fontSize: "11px",
                  fontWeight: 500,
                  opacity: 0.8,
                  marginTop: "-6px"
                }}>
                  {t("basedOnGeneratedPrompts")}
                </div>
                <div style={{
                  fontSize: "36px",
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                }}>
                  {displayStatus}
                </div>
                {recommendations.executive_summary.recommended_timeline && (
                  <div style={{
                    padding: "8px 20px",
                    background: "rgba(255,255,255,0.2)",
                    borderRadius: "999px",
                    fontSize: "14px",
                    fontWeight: 500
                  }}>
                    {recommendations.executive_summary.recommended_timeline}
                  </div>
                )}
              </div>
            );
          })()}

          <div style={{
            display: "flex",
            flexDirection: "column",
            gap: "20px"
          }}>
            {/* Key Findings from Executive Summary */}
            {recommendations.executive_summary.key_findings && Array.isArray(recommendations.executive_summary.key_findings) && recommendations.executive_summary.key_findings.length > 0 && (
              <div style={{
                background: "linear-gradient(145deg, var(--card-bg), rgba(59, 130, 246, 0.05))",
                borderRadius: "12px",
                padding: "20px",
                border: "1px solid var(--card-border)"
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
                  <FaIcon icon="fa-info-circle" color="#3b82f6" size={16} />
                  <h5 style={{ margin: 0, fontSize: "16px", fontWeight: "600", color: "var(--card-text)" }}>{t("keyFindings")}</h5>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  {recommendations.executive_summary.key_findings.map((finding, index) => (
                    <div key={index} style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "12px",
                      padding: "14px 16px",
                      background: "rgba(59, 130, 246, 0.06)",
                      border: "1px solid rgba(59, 130, 246, 0.15)",
                      borderRadius: "10px"
                    }}>
                      <div style={{
                        width: "24px",
                        height: "24px",
                        borderRadius: "6px",
                        background: "rgba(59, 130, 246, 0.15)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0
                      }}>
                        <FaIcon icon="fa-info-circle" color="#3b82f6" size={12} />
                      </div>
                      <div style={{ color: "var(--card-text)", lineHeight: 1.6, fontSize: "14px" }}>
                        {finding}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Overall Assessment inside Executive Summary */}
            {recommendations.overall_assessment && (
              <div style={{
                background: "linear-gradient(145deg, var(--card-bg), rgba(139, 92, 246, 0.05))",
                borderRadius: "12px",
                padding: "20px",
                border: "1px solid var(--card-border)"
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
                  <FaIcon icon="fa-bullseye" color="#8b5cf6" size={16} />
                  <h5 style={{ margin: 0, fontSize: "16px", fontWeight: "600", color: "var(--card-text)" }}>{t("overallAssessment")}</h5>
                </div>
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(5, 1fr)",
                  gap: "12px"
                }}>
                  {recommendations.overall_assessment.security_posture && (() => {
                    const posture = recommendations.overall_assessment.security_posture;
                    const postureStyle = getRiskLevelStyle(posture);

                    return (
                      <div style={{
                        background: postureStyle.bg,
                        borderRadius: "12px",
                        padding: "16px",
                        border: `1px solid ${postureStyle.border}`,
                        textAlign: "center"
                      }}>
                        <div style={{ fontSize: "11px", color: "var(--muted-text)", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                          {t("securityPostureRisk")}
                        </div>
                        <div style={{ fontSize: "22px", fontWeight: 700, color: postureStyle.text }}>
                          {levelDisplayNames[posture] || t("unknown")}
                        </div>
                      </div>
                    );
                  })()}
                  {recommendations.overall_assessment.total_failed_tests !== undefined && (
                    <div style={{
                      background: "rgba(239, 68, 68, 0.06)",
                      borderRadius: "12px",
                      padding: "16px",
                      border: "1px solid rgba(239, 68, 68, 0.15)",
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: "11px", color: "var(--muted-text)", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        {t("failedToBlock")}
                      </div>
                      <div style={{ fontSize: "22px", fontWeight: 700, color: "#ef4444" }}>
                        {recommendations.overall_assessment.total_failed_tests}
                      </div>
                    </div>
                  )}
                  {recommendations.overall_assessment.critical_vulnerabilities !== undefined && (
                    <div style={{
                      background: "rgba(153, 27, 27, 0.06)",
                      borderRadius: "12px",
                      padding: "16px",
                      border: "1px solid rgba(153, 27, 27, 0.15)",
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: "11px", color: "var(--muted-text)", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        {t("criticalVulnerabilities")}
                      </div>
                      <div style={{ fontSize: "22px", fontWeight: 700, color: "#991b1b" }}>
                        {recommendations.overall_assessment.critical_vulnerabilities}
                      </div>
                    </div>
                  )}
                  {recommendations.overall_assessment.overall_risk_score !== undefined && (
                    <div style={{
                      background: "rgba(139, 92, 246, 0.06)",
                      borderRadius: "12px",
                      padding: "16px",
                      border: "1px solid rgba(139, 92, 246, 0.15)",
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: "11px", color: "var(--muted-text)", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        {t("riskScore")}
                      </div>
                      <div style={{ fontSize: "22px", fontWeight: 700, color: "#8b5cf6" }}>
                        {recommendations.overall_assessment.overall_risk_score.toFixed(2)}
                      </div>
                    </div>
                  )}
                  {recommendations.overall_assessment.remediation_timeline && (
                    <div style={{
                      background: "rgba(245, 158, 11, 0.06)",
                      borderRadius: "12px",
                      padding: "16px",
                      border: "1px solid rgba(245, 158, 11, 0.15)",
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: "11px", color: "var(--muted-text)", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        {t("timeline")}
                      </div>
                      <div style={{ fontSize: "14px", fontWeight: 600, color: "#f59e0b" }}>
                        {recommendations.overall_assessment.remediation_timeline}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Quick Stats inside Executive Summary */}
            {recommendations.quick_stats && (
              <div style={{
                background: "linear-gradient(145deg, var(--card-bg), rgba(107, 114, 128, 0.05))",
                borderRadius: "12px",
                padding: "20px",
                border: "1px solid var(--card-border)"
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
                  <FaIcon icon="fa-chart-bar" color="#6b7280" size={16} />
                  <h5 style={{ margin: 0, fontSize: "16px", fontWeight: "600", color: "var(--card-text)" }}>{t("quickStats")}</h5>
                </div>
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                  gap: "12px"
                }}>
                  {recommendations.quick_stats.total_tests !== undefined && (
                    <div style={{
                      background: "rgba(59, 130, 246, 0.06)",
                      borderRadius: "12px",
                      padding: "16px",
                      border: "1px solid rgba(59, 130, 246, 0.15)",
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: "11px", color: "var(--muted-text)", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        {t("totalTests")}
                      </div>
                      <div style={{ fontSize: "22px", fontWeight: 700, color: "var(--card-text)" }}>
                        {recommendations.quick_stats.total_tests}
                      </div>
                    </div>
                  )}
                  {recommendations.quick_stats.total_failed_tests !== undefined && (
                    <div style={{
                      background: "rgba(239, 68, 68, 0.06)",
                      borderRadius: "12px",
                      padding: "16px",
                      border: "1px solid rgba(239, 68, 68, 0.15)",
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: "11px", color: "var(--muted-text)", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        {t("failedToBlockLower")}
                      </div>
                      <div style={{ fontSize: "22px", fontWeight: 700, color: "#ef4444" }}>
                        {recommendations.quick_stats.total_failed_tests}
                      </div>
                    </div>
                  )}
                  {recommendations.quick_stats.total_high_risk !== undefined && (
                    <div style={{
                      background: "rgba(234, 88, 12, 0.06)",
                      borderRadius: "12px",
                      padding: "16px",
                      border: "1px solid rgba(234, 88, 12, 0.15)",
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: "11px", color: "var(--muted-text)", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        {t("highRisk")}
                      </div>
                      <div style={{ fontSize: "22px", fontWeight: 700, color: "#ef4444" }}>
                        {recommendations.quick_stats.total_high_risk}
                      </div>
                    </div>
                  )}
                  {recommendations.quick_stats.overall_readiness !== undefined && (
                    <div style={{
                      background: "rgba(34, 197, 94, 0.06)",
                      borderRadius: "12px",
                      padding: "16px",
                      border: "1px solid rgba(34, 197, 94, 0.15)",
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: "11px", color: "var(--muted-text)", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        {t("readiness")}
                      </div>
                      <div style={{ fontSize: "22px", fontWeight: 700, color: "#22c55e" }}>
                        {recommendations.quick_stats.overall_readiness.toFixed(1)}%
                      </div>
                    </div>
                  )}
                  {recommendations.quick_stats.total_categories_tested !== undefined && (
                    <div style={{
                      background: "rgba(139, 92, 246, 0.06)",
                      borderRadius: "12px",
                      padding: "16px",
                      border: "1px solid rgba(139, 92, 246, 0.15)",
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: "11px", color: "var(--muted-text)", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        {t("categories")}
                      </div>
                      <div style={{ fontSize: "22px", fontWeight: 700, color: "#8b5cf6" }}>
                        {recommendations.quick_stats.total_categories_tested}
                      </div>
                    </div>
                  )}
                  {recommendations.quick_stats.overall_avg_response_time !== undefined && (
                    <div style={{
                      background: "rgba(245, 158, 11, 0.06)",
                      borderRadius: "12px",
                      padding: "16px",
                      border: "1px solid rgba(245, 158, 11, 0.15)",
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: "11px", color: "var(--muted-text)", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        {t("avgResponseTime")}
                      </div>
                      <div style={{ fontSize: "22px", fontWeight: 700, color: "#f59e0b" }}>
                        {recommendations.quick_stats.overall_avg_response_time.toFixed(3)}s
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Metadata inside Executive Summary */}
            {recommendations.metadata && (
              <div style={{
                background: "linear-gradient(145deg, var(--card-bg), rgba(107, 114, 128, 0.05))",
                borderRadius: "12px",
                padding: "20px",
                border: "1px solid var(--card-border)"
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
                  <FaIcon icon="fa-file-alt" color="#6b7280" size={16} />
                  <h5 style={{ margin: 0, fontSize: "16px", fontWeight: "600", color: "var(--card-text)" }}>{t("metadata")}</h5>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px" }}>
                  {recommendations.metadata.generated_at && (
                    <div style={{
                      background: "rgba(59, 130, 246, 0.06)",
                      borderRadius: "10px",
                      padding: "16px",
                      border: "1px solid rgba(59, 130, 246, 0.15)"
                    }}>
                      <div style={{ fontSize: "12px", color: "var(--muted-text)", marginBottom: "6px", textTransform: "uppercase" }}>
                        {t("generatedAt")}
                      </div>
                      <div style={{ fontSize: "14px", color: "var(--card-text)", fontWeight: 500 }}>
                        {new Date(recommendations.metadata.generated_at).toLocaleString()}
                      </div>
                    </div>
                  )}
                  {recommendations.metadata.total_tests_analyzed !== undefined && (
                    <div style={{
                      background: "rgba(34, 197, 94, 0.06)",
                      borderRadius: "10px",
                      padding: "16px",
                      border: "1px solid rgba(34, 197, 94, 0.15)"
                    }}>
                      <div style={{ fontSize: "12px", color: "var(--muted-text)", marginBottom: "6px", textTransform: "uppercase" }}>
                        {t("totalTestsAnalyzed")}
                      </div>
                      <div style={{ fontSize: "20px", color: "#22c55e", fontWeight: 700 }}>
                        {recommendations.metadata.total_tests_analyzed}
                      </div>
                    </div>
                  )}
                  {recommendations.metadata.failed_tests_analyzed !== undefined && (
                    <div style={{
                      background: "rgba(239, 68, 68, 0.06)",
                      borderRadius: "10px",
                      padding: "16px",
                      border: "1px solid rgba(239, 68, 68, 0.15)"
                    }}>
                      <div style={{ fontSize: "12px", color: "var(--muted-text)", marginBottom: "6px", textTransform: "uppercase" }}>
                        {t("failedTestsAnalyzed")}
                      </div>
                      <div style={{ fontSize: "20px", color: "#ef4444", fontWeight: 700 }}>
                        {recommendations.metadata.failed_tests_analyzed}
                      </div>
                    </div>
                  )}
                  {recommendations.metadata.categories_analyzed && Array.isArray(recommendations.metadata.categories_analyzed) && (
                    <div style={{
                      background: "rgba(139, 92, 246, 0.06)",
                      borderRadius: "10px",
                      padding: "16px",
                      border: "1px solid rgba(139, 92, 246, 0.15)",
                      gridColumn: "1 / -1"
                    }}>
                      <div style={{ fontSize: "12px", color: "var(--muted-text)", marginBottom: "8px", textTransform: "uppercase" }}>
                        {t("categoriesAnalyzed")}
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                        {recommendations.metadata.categories_analyzed.map((category, idx) => (
                          <span key={idx} style={{
                            padding: "6px 12px",
                            borderRadius: "6px",
                            fontSize: "12px",
                            background: "rgba(139, 92, 246, 0.1)",
                            color: "#8b5cf6",
                            fontWeight: 500
                          }}>
                            {formatCategoryName(category)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

          </div>
        </SectionCard>
      )}


      {/* Section 2: Categorized Analysis */}
      {recommendations.categorized_analysis && Object.keys(recommendations.categorized_analysis).length > 0 && (
        <SectionCardAccordion title={t("categorizedAnalysis")} icon={<FaIcon icon="fa-file-alt" color={SECTION_ICON_COLOR} size={SECTION_ICON_SIZE} />}>
          <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            {Object.entries(recommendations.categorized_analysis).map(([category, data]) => (
              <div key={category} style={{
                border: "1px solid var(--card-border)",
                borderRadius: "12px",
                padding: "20px",
                background: "var(--card-bg)"
              }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
                  <h4 style={{ margin: 0, fontSize: "16px", fontWeight: "600", color: "var(--card-text)", textTransform: "uppercase" }}>
                    {formatCategoryName(category)}
                  </h4>
                  <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                    {data.status && (
                      <span style={{
                        padding: "4px 12px",
                        borderRadius: "999px",
                        fontSize: "11px",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        background: data.status === "vulnerable" ? "rgba(239, 68, 68, 0.1)" : "rgba(34, 197, 94, 0.1)",
                        color: data.status === "vulnerable" ? "#ef4444" : "#22c55e"
                      }}>
                        {data.status}
                      </span>
                    )}
                    {data.risk_score !== undefined && (
                      <span style={{
                        padding: "4px 12px",
                        borderRadius: "999px",
                        fontSize: "11px",
                        fontWeight: 600,
                        background: "rgba(59, 130, 246, 0.1)",
                        color: "#3b82f6"
                      }}>
                        {t("risk")}: {data.risk_score.toFixed(1)}
                      </span>
                    )}
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: "12px", marginBottom: "16px" }}>
                  {data.total_failed !== undefined && (
                    <div style={{
                      background: "rgba(239, 68, 68, 0.06)",
                      borderRadius: "8px",
                      padding: "12px",
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: "11px", color: "var(--muted-text)", marginBottom: "4px" }}>{t("failed")}</div>
                      <div style={{ fontSize: "20px", fontWeight: 700, color: "#ef4444" }}>{data.total_failed}</div>
                    </div>
                  )}
                  {data.critical_vulnerabilities !== undefined && (
                    <div style={{
                      background: "rgba(153, 27, 27, 0.06)",
                      borderRadius: "8px",
                      padding: "12px",
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: "11px", color: "var(--muted-text)", marginBottom: "4px" }}>{t("critical")}</div>
                      <div style={{ fontSize: "20px", fontWeight: 700, color: "#991b1b" }}>{data.critical_vulnerabilities}</div>
                    </div>
                  )}
                  {data.high_vulnerabilities !== undefined && (
                    <div style={{
                      background: "rgba(234, 88, 12, 0.06)",
                      borderRadius: "8px",
                      padding: "12px",
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: "11px", color: "var(--muted-text)", marginBottom: "4px" }}>{t("high")}</div>
                      <div style={{ fontSize: "20px", fontWeight: 700, color: "#ea580c" }}>{data.high_vulnerabilities}</div>
                    </div>
                  )}
                  {data.subcategories_affected !== undefined && (
                    <div style={{
                      background: "rgba(59, 130, 246, 0.06)",
                      borderRadius: "8px",
                      padding: "12px",
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: "11px", color: "var(--muted-text)", marginBottom: "4px" }}>{t("subcategories")}</div>
                      <div style={{ fontSize: "20px", fontWeight: 700, color: "#3b82f6" }}>{data.subcategories_affected}</div>
                    </div>
                  )}
                </div>

                {data.recommendations && Array.isArray(data.recommendations) && data.recommendations.length > 0 && (() => {
                  // Sort recommendations by priority: critical, high, medium, low
                  const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
                  const sortedRecs = [...data.recommendations].sort((a, b) => {
                    const aPriority = priorityOrder[a.priority?.toLowerCase()] ?? 99;
                    const bPriority = priorityOrder[b.priority?.toLowerCase()] ?? 99;
                    return aPriority - bPriority;
                  });

                  return (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "12px" }}>
                      {sortedRecs.map((rec, idx) => {
                        const recPriority = rec.priority?.toLowerCase() || "medium";
                        const recStyle = getRiskLevelPriorityColors(recPriority);
                        return (
                          <div key={idx} style={{
                            background: recStyle.bg,
                            border: `1px solid ${recStyle.border}`,
                            borderRadius: "10px",
                            padding: "16px"
                          }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "10px" }}>
                              {getPriorityIcon(recPriority)}
                              <div style={{ fontSize: "15px", fontWeight: 600, color: recStyle.text }}>
                                {rec.action}
                              </div>
                              <span style={{
                                padding: "4px 10px",
                                borderRadius: "999px",
                                fontSize: "10px",
                                fontWeight: 600,
                                textTransform: "uppercase",
                                background: recStyle.bg,
                                color: recStyle.text,
                                border: `1px solid ${recStyle.border}`
                              }}>
                                {rec.priority}
                              </span>
                            </div>
                            {rec.description && (
                              <div style={{ color: "var(--card-text)", fontSize: "14px", marginBottom: "10px", lineHeight: 1.5 }}>
                                {rec.description}
                              </div>
                            )}
                            {rec.implementation && Array.isArray(rec.implementation) && rec.implementation.length > 0 && (
                              <div style={{ marginTop: "10px" }}>
                                <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--muted-text)", marginBottom: "8px", textTransform: "uppercase" }}>
                                  {t("implementationSteps")}
                                </div>
                                <ul style={{ margin: 0, paddingLeft: "20px", color: "var(--body-text)", fontSize: "13px", lineHeight: 1.8 }}>
                                  {rec.implementation.map((step, stepIdx) => (
                                    <li key={stepIdx}>{step}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            ))}
          </div>
        </SectionCardAccordion>
      )}

      {/* Section 3: Prioritized Actions */}
      {recommendations.prioritized_actions && Array.isArray(recommendations.prioritized_actions) && recommendations.prioritized_actions.length > 0 && (
        <SectionCardAccordion title={t("prioritizedActions")} icon={<FaIcon icon="fa-bullseye" color={SECTION_ICON_COLOR} size={SECTION_ICON_SIZE} />}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "16px" }}>
            {recommendations.prioritized_actions.map((action, index) => {
              const actionPriority = action.priority?.toLowerCase() || "medium";
              const actionStyle = getRiskLevelPriorityColors(actionPriority);
              return (
                <div key={index} style={{
                  border: "1px solid var(--card-border)",
                  borderRadius: "14px",
                  overflow: "hidden",
                  background: "var(--card-bg)"
                }}>
                  <div style={{
                    padding: "16px 20px",
                    background: actionStyle.gradient,
                    borderBottom: "1px solid var(--card-border)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "12px"
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px", flex: 1 }}>
                      <div style={{
                        width: "32px",
                        height: "32px",
                        borderRadius: "8px",
                        background: "rgba(255,255,255,0.2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                      }}>
                        {getPriorityIcon(actionPriority)}
                      </div>
                      <div style={{ fontSize: "17px", fontWeight: 600, color: "white" }}>{action.action}</div>
                    </div>
                    <span style={{
                      padding: "5px 12px",
                      borderRadius: "999px",
                      fontSize: "11px",
                      fontWeight: 600,
                      letterSpacing: "0.05em",
                      color: "white",
                      background: "rgba(255,255,255,0.2)",
                      whiteSpace: "nowrap"
                    }}>
                      {levelDisplayNames[action.priority] || t("unknown")}
                    </span>
                  </div>

                  <div style={{ padding: "20px", display: "flex", flexDirection: "column", gap: "12px" }}>
                    {action.category && (
                      <div style={{ fontSize: "12px", color: "var(--muted-text)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        Category: <span style={{ color: "var(--card-text)" }}>{formatCategoryName(action.category)}</span>
                      </div>
                    )}
                    {action.description && (
                      <div style={{ color: "var(--card-text)", lineHeight: 1.6, fontSize: "14px" }}>
                        {action.description}
                      </div>
                    )}
                    {action.implementation && Array.isArray(action.implementation) && action.implementation.length > 0 && (
                      <div style={{
                        background: "rgba(139, 92, 246, 0.04)",
                        borderRadius: "12px",
                        padding: "16px",
                        border: "1px solid rgba(139, 92, 246, 0.12)"
                      }}>
                        <div style={{
                          fontSize: "12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                          color: "#8b5cf6",
                          marginBottom: "12px",
                          fontWeight: 600,
                          display: "flex",
                          alignItems: "center",
                          gap: "8px"
                        }}>
                          <FaIcon icon="fa-bullseye" color="#8b5cf6" size={12} />
                          {t("implementationSteps")}
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                          {action.implementation.map((step, idx) => (
                            <div key={idx} style={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: "10px",
                              padding: "10px 14px",
                              background: "rgba(139, 92, 246, 0.06)",
                              borderRadius: "8px",
                              border: "1px solid rgba(139, 92, 246, 0.1)"
                            }}>
                              <div style={{ color: "#8b5cf6", marginTop: "2px", flexShrink: 0 }}>•</div>
                              <span style={{ fontSize: "14px", color: "var(--card-text)", lineHeight: 1.5 }}>{step}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </SectionCardAccordion>
      )}

      {/* Section 4: Pattern Test Results */}
      {recommendations.pattern_test_results && Object.keys(recommendations.pattern_test_results).length > 0 && (
        <SectionCardAccordion title={t("patternTestResults")} icon={<FaIcon icon="fa-search" color={SECTION_ICON_COLOR} size={SECTION_ICON_SIZE} />}>
          <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
            {Object.entries(recommendations.pattern_test_results).map(([patternType, patterns]) => {
              if (!Array.isArray(patterns) || patterns.length === 0) return null;

              // Sort patterns by matches_found in descending order
              const sortedPatterns = [...patterns].sort((a, b) => {
                const aMatches = a.matches_found ?? 0;
                const bMatches = b.matches_found ?? 0;
                return bMatches - aMatches;
              });

              return (
                <div key={patternType} style={{
                  border: "1px solid var(--card-border)",
                  borderRadius: "12px",
                  padding: "20px",
                  background: "var(--card-bg)"
                }}>
                  <h4 style={{
                    margin: "0 0 16px 0",
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "var(--card-text)",
                    textTransform: "capitalize"
                  }}>
                    {formatCategoryName(patternType)}
                  </h4>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "12px" }}>
                    {sortedPatterns.map((pattern, idx) => (
                      <div key={idx} style={{
                        background: "rgba(107, 114, 128, 0.05)",
                        borderRadius: "10px",
                        padding: "16px",
                        border: "1px solid rgba(107, 114, 128, 0.1)",
                        minWidth: 0,
                        overflow: "hidden"
                      }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
                          <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--card-text)" }}>
                            {pattern.pattern_name || `${t("pattern")} ${idx + 1}`}
                          </div>
                          {pattern.matches_found !== undefined && (
                            <span style={{
                              padding: "4px 12px",
                              borderRadius: "999px",
                              fontSize: "11px",
                              fontWeight: 600,
                              background: pattern.matches_found > 0 ? "rgba(239, 68, 68, 0.1)" : "rgba(34, 197, 94, 0.1)",
                              color: pattern.matches_found > 0 ? "#ef4444" : "#22c55e"
                            }}>
                              {pattern.matches_found} {t("matchesChip")}
                            </span>
                          )}
                        </div>
                        {pattern.pattern && (
                          <div style={{
                            background: "rgba(107, 114, 128, 0.08)",
                            padding: "12px",
                            borderRadius: "8px",
                            fontFamily: "monospace",
                            fontSize: "12px",
                            color: "var(--body-text)",
                            marginBottom: "12px",
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                            overflow: "hidden"
                          }}>
                            {pattern.pattern}
                          </div>
                        )}
                        {pattern.sample_matches && Array.isArray(pattern.sample_matches) && pattern.sample_matches.length > 0 && (
                          <div>
                            <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--muted-text)", marginBottom: "8px", textTransform: "uppercase" }}>
                              {t("sampleMatches")}
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                              {pattern.sample_matches.slice(0, 3).map((match, matchIdx) => (
                                <div key={matchIdx} style={{
                                  background: "rgba(239, 68, 68, 0.05)",
                                  padding: "10px",
                                  borderRadius: "6px",
                                  fontSize: "12px",
                                  color: "var(--body-text)",
                                  fontFamily: "monospace",
                                  wordBreak: "break-word",
                                  overflowWrap: "break-word",
                                  overflow: "hidden"
                                }}>
                                  {match.payload || (typeof match === 'string' ? match : JSON.stringify(match))}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </SectionCardAccordion>
      )}

      {/* Section 5: Regex Protections */}
      {recommendations.regex_protections && (
        <SectionCardAccordion title={t("regexProtections")} icon={<FaIcon icon="fa-code" color={SECTION_ICON_COLOR} size={SECTION_ICON_SIZE} />}>
          {recommendations.regex_protections.predefined_patterns && Object.keys(recommendations.regex_protections.predefined_patterns).length > 0 && (
            <div style={{ marginBottom: "24px", minWidth: 0, display: "flex", flexDirection: "column", gap: "10px" }}>
              <h4 style={{ margin: 0, fontSize: "13px", fontWeight: "600", color: "var(--card-text)", textTransform: "uppercase" }}>
                {t("predefinedPatterns")}
              </h4>
              <div style={{
                background: "rgba(107, 114, 128, 0.05)",
                padding: "16px",
                borderRadius: "8px",
                border: "1px solid rgba(107, 114, 128, 0.1)",
                display: "flex",
                flexDirection: "column",
                gap: "16px"
              }}>
                {Object.entries(recommendations.regex_protections.predefined_patterns).map(([category, patterns]) => (
                  <div key={category} style={{
                    background: "rgba(107, 114, 128, 0.05)",
                    borderRadius: "12px",
                    padding: "16px",
                    border: "1px solid rgba(107, 114, 128, 0.1)"
                  }}>
                    <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--card-text)", marginBottom: "12px", textTransform: "capitalize" }}>
                      {formatCategoryName(category)}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "8px" }}>
                      {Object.entries(patterns).map(([name, pattern]) => (
                        <div key={name} style={{
                          background: "rgba(107, 114, 128, 0.08)",
                          padding: "10px",
                          borderRadius: "6px",
                          fontFamily: "monospace",
                          fontSize: "12px",
                          color: "var(--body-text)",
                          minWidth: 0,
                          overflow: "hidden"
                        }}>
                          <div style={{ fontSize: "11px", color: "var(--muted-text)", marginBottom: "4px", textTransform: "capitalize" }}>
                            {name.replace(/_/g, " ")}
                          </div>
                          <div style={{ wordBreak: "break-word", overflowWrap: "break-word", overflow: "hidden" }}>{pattern}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {recommendations.regex_protections.custom_patterns && Object.keys(recommendations.regex_protections.custom_patterns).length > 0 && (
            <div style={{ marginBottom: "24px" }}>
              <h4 style={{ margin: "0 0 16px 0", fontSize: "16px", fontWeight: "600", color: "var(--card-text)" }}>
                {t("customPatterns")}
              </h4>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "8px" }}>
                {Object.entries(recommendations.regex_protections.custom_patterns).map(([name, pattern]) => (
                  <div key={name} style={{
                    background: "rgba(107, 114, 128, 0.05)",
                    padding: "12px",
                    borderRadius: "8px",
                    border: "1px solid rgba(107, 114, 128, 0.1)",
                    minWidth: 0,
                    overflow: "hidden"
                  }}>
                    <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--card-text)", marginBottom: "6px", textTransform: "capitalize" }}>
                      {name.replace(/_/g, " ")}
                    </div>
                    <div style={{
                      fontFamily: "monospace",
                      fontSize: "12px",
                      color: "var(--body-text)",
                      background: "rgba(107, 114, 128, 0.08)",
                      padding: "8px",
                      borderRadius: "6px",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                      overflow: "hidden"
                    }}>
                      {pattern}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {recommendations.regex_protections.implementation_guide && (
            <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "10px" }}>
              <h4 style={{ margin: 0, fontSize: "13px", fontWeight: "600", color: "var(--card-text)", textTransform: "uppercase" }}>
                {t("implementationGuide")}
              </h4>
              <div style={{
                background: "rgba(107, 114, 128, 0.05)",
                padding: "16px",
                borderRadius: "8px",
                border: "1px solid rgba(107, 114, 128, 0.1)",
                display: "flex",
                flexDirection: "column",
                gap: "20px"
              }}>
                {recommendations.regex_protections.implementation_guide.best_practices && Array.isArray(recommendations.regex_protections.implementation_guide.best_practices) && (
                  <div>
                    <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--card-text)", marginBottom: "12px", textTransform: "uppercase" }}>
                      {t("bestPractices")}
                    </div>
                    <div style={{
                      background: "rgba(107, 114, 128, 0.05)",
                      padding: "16px",
                      borderRadius: "8px",
                      border: "1px solid rgba(107, 114, 128, 0.1)"
                    }}>
                      <ul style={{ margin: 0, paddingLeft: "20px", color: "var(--body-text)", fontSize: "14px", lineHeight: 1.8 }}>
                        {recommendations.regex_protections.implementation_guide.best_practices.map((practice, idx) => (
                          <li key={idx}>{practice}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
                {recommendations.regex_protections.implementation_guide.python_implementation && (
                  <div>
                    <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--card-text)", marginBottom: "12px", textTransform: "uppercase" }}>
                      {t("pythonImplementation")}
                    </div>
                    <div style={{
                      background: "rgba(107, 114, 128, 0.05)",
                      padding: "16px",
                      borderRadius: "8px",
                      border: "1px solid rgba(107, 114, 128, 0.1)",
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px"
                    }}>
                      {recommendations.regex_protections.implementation_guide.python_implementation.example_code && (
                        <div>
                          <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--muted-text)", marginBottom: "8px" }}>
                            {t("exampleCode")}
                          </div>
                          <pre style={codeSnippetStyle}>
                            {recommendations.regex_protections.implementation_guide.python_implementation.example_code.trim()}
                          </pre>
                        </div>
                      )}
                      {recommendations.regex_protections.implementation_guide.python_implementation.usage_notes && Array.isArray(recommendations.regex_protections.implementation_guide.python_implementation.usage_notes) && (
                        <div>
                          <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--muted-text)", marginBottom: "8px" }}>
                            {t("usageNotes")}
                          </div>
                          <div style={{
                            background: "rgba(107, 114, 128, 0.05)",
                            padding: "16px",
                            borderRadius: "8px",
                            border: "1px solid rgba(107, 114, 128, 0.1)"
                          }}>
                            <ul style={{ margin: 0, paddingLeft: "20px", color: "var(--body-text)", fontSize: "13px", lineHeight: 1.8 }}>
                              {recommendations.regex_protections.implementation_guide.python_implementation.usage_notes.map((note, idx) => (
                                <li key={idx}>{note}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </SectionCardAccordion>
      )}

      {/* Section 6: WAF Rules */}
      {recommendations.waf_rules && (
        <SectionCardAccordion title={t("wafRules")} icon={<FaIcon icon="fa-shield-alt" color={SECTION_ICON_COLOR} size={SECTION_ICON_SIZE} />}>
          {recommendations.waf_rules.rule_sets && Object.keys(recommendations.waf_rules.rule_sets).length > 0 && (
            <div style={{ marginBottom: "24px" }}>
              <h4 style={{ margin: "0 0 16px 0", fontSize: "16px", fontWeight: "600", color: "var(--card-text)" }}>
                {t("ruleSets")}
              </h4>
              <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                {Object.entries(recommendations.waf_rules.rule_sets).map(([ruleSetName, ruleSet]) => (
                  <div key={ruleSetName} style={{
                    border: "1px solid var(--card-border)",
                    borderRadius: "12px",
                    padding: "20px",
                    background: "var(--card-bg)"
                  }}>
                    <div style={{ fontSize: "15px", fontWeight: 600, color: "var(--card-text)", marginBottom: "8px", textTransform: "capitalize" }}>
                      {formatCategoryName(ruleSetName)}
                    </div>
                    {ruleSet.description && (
                      <div style={{ fontSize: "13px", color: "var(--muted-text)", marginBottom: "16px" }}>
                        {ruleSet.description}
                      </div>
                    )}
                    {ruleSet.rules && Array.isArray(ruleSet.rules) && ruleSet.rules.length > 0 && (
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "12px" }}>
                        {ruleSet.rules.map((rule, index) => (
                          <div key={index} style={{
                            border: "1px solid var(--card-border)",
                            borderRadius: "8px",
                            padding: "16px",
                            background: "rgba(59, 130, 246, 0.03)",
                            display: "flex",
                            flexDirection: "column",
                            gap: "12px",
                            minWidth: 0,
                            overflow: "hidden"
                          }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px", flexWrap: "wrap" }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--card-text)", marginBottom: "8px" }}>
                                  {rule.name}
                                </div>
                              </div>
                              <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                                <span style={{
                                  padding: "4px 10px",
                                  borderRadius: "999px",
                                  fontSize: "10px",
                                  fontWeight: 600,
                                  textTransform: "uppercase",
                                  color: rule.action === "BLOCK" || rule.action === "block" ? "#ef4444" : "#10b981",
                                  background: rule.action === "BLOCK" || rule.action === "block" ? "rgba(239, 68, 68, 0.1)" : "rgba(16, 185, 129, 0.1)",
                                  whiteSpace: "nowrap"
                                }}>
                                  {rule.action}
                                </span>
                                {rule.score && (
                                  <div style={{
                                    padding: "4px 10px",
                                    borderRadius: "6px",
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    color: "var(--card-text)",
                                    background: "rgba(59, 130, 246, 0.1)",
                                    whiteSpace: "nowrap"
                                  }}>
                                    {t("score")}: {rule.score}
                                  </div>
                                )}
                              </div>
                            </div>
                            {rule.pattern && (
                              <div style={{ marginTop: "4px" }}>
                                <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--muted-text)", marginBottom: "6px", textTransform: "uppercase" }}>
                                  {t("pattern")}
                                </div>
                                <pre style={codeCompactStyle}>
                                  {rule.pattern}
                                </pre>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {recommendations.waf_rules.custom_rules && Array.isArray(recommendations.waf_rules.custom_rules) && recommendations.waf_rules.custom_rules.length > 0 && (
            <div style={{ marginBottom: "24px" }}>
              <h4 style={{ margin: "0 0 16px 0", fontSize: "16px", fontWeight: "600", color: "var(--card-text)" }}>
                {t("customRules")}
              </h4>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "12px" }}>
                {recommendations.waf_rules.custom_rules.map((rule, index) => (
                  <div key={index} style={{
                    border: "1px solid var(--card-border)",
                    borderRadius: "8px",
                    padding: "16px",
                    background: "rgba(59, 130, 246, 0.03)",
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                    minWidth: 0,
                    overflow: "hidden"
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px", flexWrap: "wrap" }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--card-text)", marginBottom: "8px", fontFamily: "monospace" }}>
                          {rule.name}
                        </div>
                      </div>
                      <span style={{
                        padding: "4px 10px",
                        borderRadius: "999px",
                        fontSize: "10px",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        color: rule.action === "BLOCK" || rule.action === "block" ? "#ef4444" : "#10b981",
                        background: rule.action === "BLOCK" || rule.action === "block" ? "rgba(239, 68, 68, 0.1)" : "rgba(16, 185, 129, 0.1)",
                        whiteSpace: "nowrap"
                      }}>
                        {rule.action}
                      </span>
                    </div>
                    {rule.description && (
                      <div style={{ fontSize: "13px", color: "var(--body-text)", lineHeight: 1.5 }}>
                        {rule.description}
                      </div>
                    )}
                    {rule.pattern && (
                      <div style={{ marginTop: "4px" }}>
                        <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--muted-text)", marginBottom: "6px", textTransform: "uppercase" }}>
                          {t("pattern")}
                        </div>
                        <pre style={codeCompactStyle}>
                          {rule.pattern}
                        </pre>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {recommendations.waf_rules.deployment_guide && (
            <div>
              <h4 style={{ margin: "0 0 16px 0", fontSize: "16px", fontWeight: "600", color: "var(--card-text)" }}>
                {t("deploymentGuide")}
              </h4>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "16px" }}>
                {Object.entries(recommendations.waf_rules.deployment_guide).map(([platform, guide]) => (
                  <div key={platform} style={{
                    background: "rgba(59, 130, 246, 0.05)",
                    borderRadius: "12px",
                    padding: "20px",
                    border: "1px solid rgba(59, 130, 246, 0.15)",
                    minWidth: 0,
                    overflow: "hidden"
                  }}>
                    <div style={{ fontSize: "15px", fontWeight: 600, color: "var(--card-text)", marginBottom: "12px", textTransform: "uppercase" }}>
                      {formatCategoryName(platform)}
                    </div>
                    {guide.deployment_steps && Array.isArray(guide.deployment_steps) && (
                      <div style={{ marginBottom: "16px" }}>
                        <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--muted-text)", marginBottom: "8px", }}>
                          {t("deploymentSteps")}
                        </div>
                        <ol style={{ margin: 0, paddingLeft: "20px", color: "var(--body-text)", fontSize: "13px", lineHeight: 1.8 }}>
                          {guide.deployment_steps.map((step, idx) => (
                            <li key={idx}>{step}</li>
                          ))}
                        </ol>
                      </div>
                    )}
                    {guide.rules && Array.isArray(guide.rules) && guide.rules.length > 0 && (
                      <div>
                        <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--muted-text)", marginBottom: "8px", }}>
                          {t("exampleRules")}
                        </div>
                        <pre style={codeCompactStyle}>
                          {JSON.stringify(guide.rules, null, 2)}
                        </pre>
                      </div>
                    )}
                    {((guide.recommended_settings && Object.keys(guide.recommended_settings).length > 0) || (guide.monitoring_recommendations && Array.isArray(guide.monitoring_recommendations) && guide.monitoring_recommendations.length > 0)) && (
                      <div style={{ marginTop: "20px", display: "flex", flexDirection: "column", gap: "20px" }}>
                        {guide.recommended_settings && Object.keys(guide.recommended_settings).length > 0 && (
                          <div>
                            <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--card-text)", marginBottom: "12px" }}>
                              {t("recommendedSettings")}
                            </div>
                            <div style={{
                              background: "rgba(107, 114, 128, 0.05)",
                              padding: "16px",
                              borderRadius: "8px",
                              display: "flex",
                              flexDirection: "column",
                              gap: "10px"
                            }}>
                              {Object.entries(guide.recommended_settings).map(([key, value]) => (
                                <div key={key} style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "flex-start",
                                  gap: "16px",
                                  paddingBottom: "10px",
                                  borderBottom: "1px solid rgba(107, 114, 128, 0.1)"
                                }}>
                                  <strong style={{
                                    color: "var(--card-text)",
                                    fontSize: "13px",
                                    textTransform: "capitalize",
                                    fontWeight: 500
                                  }}>
                                    {key.replace(/_/g, " ")}
                                  </strong>
                                  <span style={{
                                    color: "var(--body-text)",
                                    fontSize: "13px",
                                    textAlign: "right",
                                    flex: 1,
                                    maxWidth: "60%"
                                  }}>
                                    {value}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {guide.monitoring_recommendations && Array.isArray(guide.monitoring_recommendations) && guide.monitoring_recommendations.length > 0 && (
                          <div>
                            <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--card-text)", marginBottom: "12px" }}>
                              {t("monitoringRecommendations")}
                            </div>
                            <div style={{
                              background: "rgba(107, 114, 128, 0.05)",
                              padding: "16px",
                              borderRadius: "8px"
                            }}>
                              <ul style={{
                                margin: 0,
                                paddingLeft: "20px",
                                color: "var(--body-text)",
                                fontSize: "13px",
                                lineHeight: 1.8,
                                display: "flex",
                                flexDirection: "column",
                                gap: "8px"
                              }}>
                                {guide.monitoring_recommendations.map((rec, idx) => (
                                  <li key={idx} style={{ paddingLeft: "4px" }}>{rec}</li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {recommendations.waf_rules.cloud_specific_rules && Object.keys(recommendations.waf_rules.cloud_specific_rules).length > 0 && (
            <div style={{ marginTop: "24px" }}>
              <h4 style={{ margin: "0 0 16px 0", fontSize: "16px", fontWeight: "600", color: "var(--card-text)" }}>
                {t("cloudSpecificRules")}
              </h4>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "16px" }}>
                {Object.entries(recommendations.waf_rules.cloud_specific_rules).map(([cloud, config]) => (
                  <div key={cloud} style={{
                    background: "rgba(107, 114, 128, 0.05)",
                    borderRadius: "12px",
                    padding: "16px",
                    border: "1px solid rgba(107, 114, 128, 0.1)",
                    minWidth: 0,
                    overflow: "hidden"
                  }}>
                    <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--card-text)", marginBottom: "12px", textTransform: "capitalize" }}>
                      {formatCategoryName(cloud)}
                    </div>
                    <pre style={codeCompactStyle}>
                      {JSON.stringify(config, null, 2)}
                    </pre>
                  </div>
                ))}
              </div>
            </div>
          )}
        </SectionCardAccordion>
      )}

      {/* Section 7: Python Security Implementation */}
      {recommendations.python_security_implementation && (() => {
        const psi = recommendations.python_security_implementation;
        const embeddingSections = [
          { key: "sync_agent_embedding", label: t("syncAgentEmbedding") },
          { key: "async_agent_embedding", label: t("asyncAgentEmbedding") },
          { key: "document_handling", label: t("documentHandling") }
        ];

        return (
          <SectionCardAccordion
            title={t("pythonSecurityRecommendations")}
            icon={<FaIcon icon="fa-terminal" color={SECTION_ICON_COLOR} size={SECTION_ICON_SIZE} />}
          >
            {((psi.title || psi.purpose) ||
              (psi.configuration && typeof psi.configuration === "object" && Object.keys(psi.configuration).length > 0)) && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "16px" }}>
                {(psi.title || psi.purpose) && (
                  <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "10px" }}>
                    <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--card-text)", textTransform: "uppercase" }}>
                      {t("overview")}
                    </div>
                    <div style={{
                      background: "rgba(107, 114, 128, 0.05)",
                      borderRadius: "8px",
                      border: "1px solid rgba(107, 114, 128, 0.1)",
                      padding: "16px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                      flex: 1
                    }}>
                      {psi.title && (
                        <div>
                          <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--muted-text)", marginBottom: "4px", textTransform: "uppercase" }}>
                            {t("title")}
                          </div>
                          <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--card-text)", lineHeight: 1.5 }}>
                            {psi.title}
                          </div>
                        </div>
                      )}
                      {psi.purpose && (
                        <div>
                          <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--muted-text)", marginBottom: "4px", textTransform: "uppercase" }}>
                            {t("purpose")}
                          </div>
                          <div style={{ fontSize: "14px", color: "var(--body-text)", lineHeight: 1.6 }}>
                            {psi.purpose}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {psi.configuration && typeof psi.configuration === "object" && Object.keys(psi.configuration).length > 0 && (
                  <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "10px" }}>
                    <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--card-text)", textTransform: "uppercase" }}>
                      {t("configuration")}
                    </div>
                    <div style={{
                      background: "rgba(107, 114, 128, 0.05)",
                      borderRadius: "8px",
                      border: "1px solid rgba(107, 114, 128, 0.1)",
                      padding: "16px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                      flex: 1
                    }}>
                      {Object.entries(psi.configuration).map(([key, value]) => (
                        <div key={key}>
                          <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--muted-text)", marginBottom: "4px", textTransform: "uppercase" }}>
                            {key.replace(/_/g, " ")}
                          </div>
                          <div style={{ fontSize: "14px", color: "var(--body-text)", lineHeight: 1.6 }}>
                            {value}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {((psi.deployment_checklist && Array.isArray(psi.deployment_checklist) && psi.deployment_checklist.length > 0) ||
              (psi.limitations && Array.isArray(psi.limitations) && psi.limitations.length > 0)) && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "16px" }}>
                {psi.deployment_checklist && Array.isArray(psi.deployment_checklist) && psi.deployment_checklist.length > 0 && (
                  <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "10px" }}>
                    <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--card-text)", textTransform: "uppercase" }}>
                      {t("deploymentChecklist")}
                    </div>
                    <div style={{
                      background: "rgba(107, 114, 128, 0.05)",
                      padding: "16px",
                      borderRadius: "8px",
                      border: "1px solid rgba(107, 114, 128, 0.1)",
                      flex: 1
                    }}>
                      <ul style={{ margin: 0, paddingLeft: "20px", color: "var(--body-text)", fontSize: "13px", lineHeight: 1.8 }}>
                        {psi.deployment_checklist.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
                {psi.limitations && Array.isArray(psi.limitations) && psi.limitations.length > 0 && (
                  <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "10px" }}>
                    <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--card-text)", textTransform: "uppercase" }}>
                      {t("limitations")}
                    </div>
                    <div style={{
                      background: "rgba(107, 114, 128, 0.05)",
                      padding: "16px",
                      borderRadius: "8px",
                      border: "1px solid rgba(107, 114, 128, 0.1)",
                      flex: 1
                    }}>
                      <ul style={{ margin: 0, paddingLeft: "20px", color: "var(--body-text)", fontSize: "13px", lineHeight: 1.8 }}>
                        {psi.limitations.map((limitation, idx) => (
                          <li key={idx}>{limitation}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            )}

            {psi.runtime_requirements && Array.isArray(psi.runtime_requirements) && psi.runtime_requirements.length > 0 && (
              <div>
                <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--card-text)", marginBottom: "10px", textTransform: "uppercase" }}>
                  {t("runtimeRequirements")}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                  {psi.runtime_requirements.map((req, idx) => (
                    <span key={idx} style={{
                      padding: "6px 12px",
                      borderRadius: "8px",
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#3b82f6",
                      background: "rgba(59, 130, 246, 0.1)",
                      border: "1px solid rgba(59, 130, 246, 0.15)"
                    }}>
                      {req}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {psi.code_snippet && (
              <div>
                <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--card-text)", marginBottom: "12px", textTransform: "uppercase" }}>
                  {t("codeSnippet")}
                </div>
                <pre style={codeSnippetStyle}>
                  {psi.code_snippet.trim()}
                </pre>
              </div>
            )}

            {embeddingSections.map(({ key, label }) => {
              const embedding = psi[key];
              if (!embedding) return null;
              return (
                <div key={key} style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "10px" }}>
                  <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--card-text)", textTransform: "uppercase" }}>
                    {label}
                  </div>
                  <div style={{
                    background: "rgba(107, 114, 128, 0.05)",
                    padding: "16px",
                    borderRadius: "8px",
                    border: "1px solid rgba(107, 114, 128, 0.1)",
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px"
                  }}>
                    {embedding.instructions && Array.isArray(embedding.instructions) && embedding.instructions.length > 0 && (
                      <div>
                        <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--muted-text)", marginBottom: "8px", textTransform: "uppercase" }}>
                          {t("instructions")}
                        </div>
                        <div style={{
                          background: "rgba(107, 114, 128, 0.05)",
                          padding: "16px",
                          borderRadius: "8px",
                          border: "1px solid rgba(107, 114, 128, 0.1)"
                        }}>
                          <ul style={{ margin: 0, paddingLeft: "20px", color: "var(--body-text)", fontSize: "13px", lineHeight: 1.8 }}>
                            {embedding.instructions.map((instruction, idx) => (
                              <li key={idx}>{instruction}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                    {embedding.example && (
                      <div>
                        <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--muted-text)", marginBottom: "8px", textTransform: "uppercase" }}>
                          {t("example")}
                        </div>
                        <pre style={codeSnippetStyle}>
                          {embedding.example.trim()}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </SectionCardAccordion>
        );
      })()}

    </div>
  );
};

export default PromptsDocsRecs;
